import { httpClient } from '../../../shared/api/httpClient';
import { adaptDeviceTokenResponse } from '../../../shared/api/intelliSightAdapters';
import type { TokenResponse } from '../../../shared/api/contracts/styleTransferSchemas';

const DEVICE_STORAGE_KEY = 'photo-studio-device-id';

function generateDeviceId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getDeviceIdentifier() {
  if (typeof window === 'undefined') {
    return generateDeviceId();
  }

  try {
    const existing = window.localStorage.getItem(DEVICE_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const generated = generateDeviceId();
    window.localStorage.setItem(DEVICE_STORAGE_KEY, generated);
    return generated;
  } catch {
    return generateDeviceId();
  }
}

function detectDeviceType(userAgent: string): string {
  if (/iphone|ipad|ipod/i.test(userAgent)) return 'iOS';
  if (/android/i.test(userAgent)) return 'Android';
  if (/windows/i.test(userAgent)) return 'Windows';
  if (/mac os/i.test(userAgent)) return 'macOS';
  if (/linux/i.test(userAgent)) return 'Linux';
  return 'Web';
}

function detectOsVersion(userAgent: string): string {
  const match = userAgent.match(/(OS|Android|Windows NT|Mac OS X|Linux)[\s/_]?([0-9._]+)/i);
  if (!match) {
    return 'unknown';
  }
  return `${match[1]} ${match[2]}`.trim();
}

function buildDeviceRequest() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : 'server';
  const deviceType = detectDeviceType(ua);
  const osVersion = detectOsVersion(ua);
  const deviceModel = typeof navigator !== 'undefined' ? navigator.platform ?? 'browser' : 'server';

  return {
    deviceType,
    deviceModel,
    osVersion,
    deviceId: getDeviceIdentifier(),
    appVersion: typeof navigator !== 'undefined' ? navigator.appVersion : undefined,
    appBuildNumber: undefined
  };
}

export async function requestDeviceToken(): Promise<TokenResponse> {
  const response = await httpClient.post('/api/v1/tokens/device', {
    json: buildDeviceRequest()
  });
  return adaptDeviceTokenResponse(response);
}
